import conductionLogo from '@/assets/images/conduction-mantine-logo.png';
import SongGrid from '@/components/SongGrid';
import { SessionContext } from "@/context/SessionContext";
import {
	Box, Button,
	Container,
	Group,
	Overlay,
	Stack,
	Text,
	Title
} from '@mantine/core';
import React, { useContext } from 'react';
import { filterByClassical, filterByPiano, sortByNewReleases, useRecommendedSongs } from '../javascript/song-sorters';


export function HeroImageBackground() {

	return (
		<Box
			pos="relative"
			pt={180}
			pb={130}
			style={{
				backgroundImage: `url(${conductionLogo})`,
				backgroundSize: 'cover',
				backgroundPosition: 'center',
			}}
		>
			<Overlay color="#000" opacity={0.65} zIndex={1} />
			<Box pos="relative" style={{ zIndex: 1 }}>
				<Title
					fw={800}
					fz={40}
					lh={1.1}
					px="md"
					c="white"
					ta="center"
					style={{ fontFamily: 'Outfit, var(--mantine-font-family)' }}
					mb={8}
				>
					Welcome to the Conduction Marketplace
				</Title>
				<Container size={640} ta="left">
					<Text size="lg" c="#fff" ta="center">
						Browse and Purchase Sheet Music for Personal or Educational Use on Conduction Education
					</Text>
				</Container>
				<Group mt={48} justify="center" px="md" gap="md">
					<Button
						h={42}
						c="black"
						bg="rgba(255,255,255,0.4)"
						fz={16}
						variant="white"
						size="lg"
						style={{ transition: 'background 0.2s, color 0.2s' }}
					>
						Marketplace Tutorial
					</Button>
					<Button
						h={42}
						c="black"
						bg="white"
						fz={16}
						variant="filled"
						size="lg"
						component="a"
						href="https://www.conduction.live/"
						target="_blank"
						rel="noopener noreferrer"
						style={{ transition: 'background 0.2s, color 0.2s' }}
					>
						Conduction Education
					</Button>
				</Group>
			</Box>
		</Box>
	);
}

const Home: React.FC = () => {
	const session = useContext(SessionContext);
	if (!session) return null;

	const allSongs = session?.marketplaceSongs ?? [];

	const newReleases = sortByNewReleases(allSongs).slice(0, 6);
	const pianoSongs = filterByPiano(allSongs).slice(0, 6);
	const classicalSongs = filterByClassical(allSongs).slice(0, 6);

	const userInterests = session?.userInterests;
	const recommended = useRecommendedSongs(allSongs);

	const isLoggedIn = !!session.user;
	const hasPersonalization =
		!!userInterests &&
		(
			userInterests.artists.length > 0 ||
			userInterests.genres.length > 0 ||
			userInterests.instruments.length > 0 ||
			!!userInterests.skillLevel
		);

	function MainHeaderBox() {
		return (
			<Box
				bg="#252841"
				c="white"
				p="xl"
				w="100%"
				maw={900}
				ml={0}
				style={{
					boxSizing: 'border-box',
				}}
			>
				<Text fz="2.5rem" fw={800} mb="md" ta="left">
					Browse Sheet Music Below
				</Text>
				<Box
					bg="rgba(255,255,255,0.12)"
					display="inline-block"
					px={20}
					py={8}
					mt={12}
					style={{ fontSize: '1.1rem', fontWeight: 500, textAlign: 'left' }}
				>
					...Or use our navigation filters
				</Box>
			</Box>
		);
	}

	function FooterPromo() {
		return (
			<>
				<Box
					w="100%"
					bg="#252841"
					c="white"
					ta="center"
					py={40}
					fw={800}
					style={{
						fontStyle: 'italic',
						fontSize: '2rem',
						marginTop: 24,
						letterSpacing: 2,
					}}
				>
					Unlock Your Students' Musical Potential!
				</Box>
				<Box
					w="100%"
					bg="#252841"
					c="white"
					ta="center"
					py={0}
					fw={800}
					style={{
						fontStyle: 'italic',
						fontSize: '1rem',
						marginTop: 16,
						letterSpacing: 2,
					}}
				>
					Why stop at notes on a page? <br />
					Conduction lets you lead, remix, and collaborate live — turning your classroom into a creative launchpad.
				</Box>
				<Box
					w="100%"
					bg="#252841"
					ta="center"
					pb={32}
				>
					<Button
						size="xl"
						variant="outline"
						color="white"
						component="a"
						href="https://www.conduction.live/"
						target="_blank"
						rel="noopener noreferrer"
						radius="md"
						mt={16}
						fz="1.3rem"
						fw={700}
						px={40}
						style={{
							border: '2px solid #fff',
							paddingTop: 16,
							paddingBottom: 16,
						}}
					>
						Level Up with Conduction &rarr;
					</Button>
				</Box>
			</>
		);
	}

	function Section({
		title,
		children,
	}: { title: string; children: React.ReactNode }) {
		return (
			<Stack
				p="md"
				bg="#252841"
				mt={40}
				w="100%"
			>
				<Group>
					<Title
						order={3}
						fz={32}
						mt={5}
						mb={30}
						ml="3rem"
						ta="left"
						fw={800}
						px={30}
						py={8}
						style={{
							border: '2px solid #fff',
							borderRadius: 5,
							background: 'transparent',
							color: '#fff',
							display: 'inline-block',
						}}
					>
						{title}
					</Title>
				</Group>
				{children}
			</Stack>
		);
	}

	return (
		<>
			<HeroImageBackground />
			<Box h={40} />
			<MainHeaderBox />

			<Section title="New Releases">
				<SongGrid songs={newReleases} type="marketplace" skeletonSize={6} />
			</Section>
			<Section title="Sheet Music for the Piano">
				<SongGrid songs={pianoSongs} type="marketplace" skeletonSize={6} />
			</Section>
			<Section title="Classical Songs">
				<SongGrid songs={classicalSongs} type="marketplace" skeletonSize={6} />
			</Section>
			{isLoggedIn && hasPersonalization && (
				<Section title="Recommended Songs">
					<SongGrid songs={recommended} type="marketplace" skeletonSize={6} />
				</Section>
			)}
			<FooterPromo />
		</>
	);
};

export default Home;